import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import Footer from "../../components/landing/Footer";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import { savePayLaterSession } from "../../utils/conferencePayLaterSession";
import SuccessState from "../../components/registration_success/SuccessState";
import PendingState from "../../components/registration_success/PendingState";
import TimeoutState from "../../components/registration_success/TimeoutState";
import ErrorState from "../../components/registration_success/ErrorState";
import PayLaterState from "../../components/registration_success/PayLaterState";
import FeaturedEventsList from "../../components/registration_success/FeaturedEventsList";
import WhatsNextInfo from "../../components/registration_success/WhatsNextInfo";

const VERIFICATION_STATES = {
  VERIFIED: "verified",
  PENDING: "pending",
  TIMEOUT: "timeout",
  ERROR: "error",
  PAY_LATER: "pay_later",
};

const MAX_VERIFICATION_CYCLES = 5;
const POLLING_INTERVAL = 8000;

export default function RegistrationSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationId, setApplicationId] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  const [verificationState, setVerificationState] = useState(
    VERIFICATION_STATES.VERIFIED
  );
  const [currentCycle, setCurrentCycle] = useState(1);
  const [isManualVerifying, setIsManualVerifying] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);

  useEffect(() => {
    const stateData = location.state || {};
    const appId =
      stateData.applicationId || `#${Math.floor(Math.random() * 10000)}GHC`;
    const regId = stateData.registrationId || null;

    setApplicationId(appId);
    setRegistrationId(regId);

    const isTradeMission = eventDetails?.event_type?.some(
      (type) => type === "trade_mission" || type === "trade mission"
    );

    // Check for "Pay Later" scenario first (most specific)
    if (stateData.payLater === true && stateData.paymentVerified === false) {
      setVerificationState(VERIFICATION_STATES.PAY_LATER);
    } else if (isTradeMission) {
      setVerificationState(VERIFICATION_STATES.VERIFIED);
    } else if (stateData.paymentVerified === true) {
      setVerificationState(VERIFICATION_STATES.VERIFIED);
    } else if (stateData.paymentVerified === false && regId) {
      setVerificationState(VERIFICATION_STATES.PENDING);
    } else {
      setVerificationState(VERIFICATION_STATES.VERIFIED);
    }
  }, [location.state, eventDetails]);

  const verifyPayment = useCallback(async () => {
    if (!registrationId || verificationState !== VERIFICATION_STATES.PENDING)
      return;

    if (currentCycle > MAX_VERIFICATION_CYCLES) {
      setVerificationState(VERIFICATION_STATES.TIMEOUT);
      return;
    }

    try {
      const response =
        await conferenceAndTradeMissionService.verifyPaymentStatus(
          registrationId
        );

      if (response.status && response.data?.payment_verified) {
        setVerificationState(VERIFICATION_STATES.VERIFIED);
        toast.success("Payment verified successfully!");
      } else {
        setCurrentCycle((prev) => prev + 1);
      }
    } catch (error) {
      if (currentCycle >= MAX_VERIFICATION_CYCLES) {
        setVerificationState(VERIFICATION_STATES.TIMEOUT);
      } else {
        setCurrentCycle((prev) => prev + 1);
      }
    }
  }, [registrationId, verificationState, currentCycle]);

  useEffect(() => {
    const isTradeMission = eventDetails?.event_type?.some(
      (type) => type === "trade_mission" || type === "trade mission"
    );

    if (
      verificationState === VERIFICATION_STATES.PENDING &&
      registrationId &&
      !isTradeMission
    ) {
      const intervalId = setInterval(verifyPayment, POLLING_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [verificationState, registrationId, verifyPayment, eventDetails]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(
          slug
        );
        if (response.data?.status_code === 200) {
          setEventDetails(response.data.data);
          if (response.data.data?.similar_event) {
            setFeaturedEvents(response.data.data.similar_event || []);
          }
        }
      } catch (error) {
        console.error("Error fetching event data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [slug]);

  // Persist pay-later flow so Payment page can restore when user clicks Pay now again (or after refresh)
  // Use eventData from location.state if available (passed from ConferencePayment), else from eventDetails fetch
  useEffect(() => {
    if (verificationState !== VERIFICATION_STATES.PAY_LATER || !slug) return;
    const stateData = location.state || {};
    const registrationData = stateData.registrationData;
    const eventDataFromState = stateData.eventData;
    const eventObj =
      eventDataFromState ??
      (eventDetails ? (eventDetails?.data?.event ?? eventDetails?.event ?? eventDetails) : null);
    if (registrationData && eventObj) {
      savePayLaterSession(slug, {
        registrationData,
        eventData: eventObj,
        isGroup: stateData.isGroup,
        numberOfAttendees: stateData.numberOfAttendees,
        totalAmount: stateData.totalAmount,
        attendees: stateData.attendees,
        selectedTicket: stateData.selectedTicket,
      });
    }
  }, [verificationState, slug, eventDetails, location.state]);

  const handleDownloadReceipt = () => {
    toast.info("Receipt download feature coming soon");
  };

  const handleManualVerification = async () => {
    if (!registrationId) {
      toast.error("Registration ID not found");
      return;
    }

    setIsManualVerifying(true);
    try {
      const response =
        await conferenceAndTradeMissionService.manualPaymentConfirmation(
          registrationId
        );

      if (response.status) {
        toast.success(
          response.message || "Manual verification submitted successfully!"
        );
        setVerificationState(VERIFICATION_STATES.VERIFIED);
      } else {
        toast.error(response.message || "Failed to submit manual verification");
      }
    } catch (error) {
      toast.error("An error occurred during manual verification");
    } finally {
      setIsManualVerifying(false);
    }
  };

  const renderStatusContent = () => {
    const isTradeMission = eventDetails?.event_type?.some(
      (type) => type === "trade_mission" || type === "trade mission"
    );

    const currentState = isTradeMission
      ? VERIFICATION_STATES.VERIFIED
      : verificationState;

    switch (currentState) {
      case VERIFICATION_STATES.VERIFIED:
        return (
          <SuccessState
            applicationId={applicationId}
            registrationId={registrationId}
            eventDetails={eventDetails}
            verificationState={verificationState}
            locationState={location.state}
            onDownloadReceipt={handleDownloadReceipt}
            onNavigate={navigate}
          />
        );

      case VERIFICATION_STATES.PAY_LATER:
        return (
          <PayLaterState
            applicationId={applicationId}
            eventDetails={eventDetails}
            registrationData={location.state?.registrationData}
            slug={slug}
            locationState={location.state}
            onNavigate={navigate}
          />
        );

      case VERIFICATION_STATES.PENDING:
        return (
          <PendingState
            applicationId={applicationId}
            currentCycle={currentCycle}
            onNavigate={navigate}
          />
        );

      case VERIFICATION_STATES.TIMEOUT:
        return (
          <TimeoutState
            applicationId={applicationId}
            isManualVerifying={isManualVerifying}
            onManualVerify={handleManualVerification}
            onNavigate={navigate}
          />
        );

      case VERIFICATION_STATES.ERROR:
        return (
          <ErrorState applicationId={applicationId} onNavigate={navigate} />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="px-4 py-8 mt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <motion.button
                onClick={() => navigate("/conference-trade-mission")}
                whileHover={{ x: -4 }}
                className="flex gap-2 items-center px-4 py-2 mb-8 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
              >
                <ChevronLeft size={18} />
                Back to Events
              </motion.button>

              <AnimatePresence mode="wait">
                {renderStatusContent()}
              </AnimatePresence>

              {(verificationState === VERIFICATION_STATES.VERIFIED || verificationState === VERIFICATION_STATES.PAY_LATER) && (
                <WhatsNextInfo />
              )}
            </div>

            <div className="lg:col-span-1">
              <FeaturedEventsList
                events={featuredEvents}
                loading={loading}
                onNavigate={navigate}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
